/**
 * Environment configuration
 *
 * .env file variables:
 *   REACT_APP_TOKEN_SERVICE_BASE_URL   - Token service base URL
 *   REACT_APP_WITMEG_ORGSERVER_BASEURL - Org registry microservice base URL
 *
 * Dev  base URL: https://deeplink.throbox.com
 * Prod base URL: https://action.rewardstick.com
 */

export const ENV = {
  TOKEN_SERVICE_URL: process.env.REACT_APP_TOKEN_SERVICE_BASE_URL,
  ORGSERVER_URL: process.env.REACT_APP_WITMEG_ORGSERVER_BASEURL,

  // Automatically detect the current site's base URL (works for both dev + prod)
  SITE_BASE_URL: window.location.origin,
};

/**
 * Fetches a short-lived bearer token from the token service.
 * Used to authenticate calls to the Org Server.
 *
 * @param {string} serviceName - e.g. "orgregistry"
 * @returns {Promise<string>} - the access token
 */
export async function fetchServiceToken(serviceName = "orgregistry") {
  const res = await fetch(`${ENV.TOKEN_SERVICE_URL}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      isLoginServerRequest: false,
      serviceName,
    }),
  });

  if (!res.ok) {
    throw new Error(`Token service error: ${res.status}`);
  }

  const data = await res.json();

  if (!data.success || !data.token) {
    throw new Error("Token service returned no token");
  }

  return data.token;
}

/**
 * Validates a magic link key against the Org Server.
 *
 * @param {string} magicLinkKey - the param1 from /mlink/{param1}
 * @param {string} token        - bearer token from fetchServiceToken()
 * @param {string} fullUrl      - the full /mlink/ URL (used as X-RequestSource)
 * @returns {Promise<{ Status: boolean, Result: string }>}
 */
export async function validateMagicLink(magicLinkKey, token, fullUrl) {
  const res = await fetch(
    `${ENV.ORGSERVER_URL}/v1/magic/validate/link`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-Match-Key": magicLinkKey,
        "X-RequestSource": fullUrl,
      },
      body: JSON.stringify({ MagicLinkKey: magicLinkKey }),
    }
  );

  if (!res.ok) {
    throw new Error(`Org server error: ${res.status}`);
  }

  return await res.json(); // { Status: boolean, Result: string }
}
